// usePDFEditorLogic.ts - Custom hook for all PDFEditor state, effects, and handlers
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getProgramReportById, getProgramReportChangeLog, updateReportStatus, updateProgramReport } from '../../../utils/api/endpoints/program-reports/program-reports';
import axios from '../../../utils/api/axios';
import useUserCredentials from '../../../utils/hooks/user/useUserCredentials';
import { useReactToPrint } from 'react-to-print';
import { UserRole } from '../../../utils/api/endpoints/user/credentials';
import { ReportData, DetailedFinding, Report } from '../types/report.types';
import { ProgramReportStatus } from '../types/report.types';
import { hasZeroFindings, getAppropriatePreviewMode, calculateSeverityCounts, filterReportDataByRunId, getFindingStableKey } from '../utils/findingsUtils';
import { parseCVSSVector } from '../../../components/forms/inputs/SeverityScoreSelector';
import { CVSS31 } from '../../../utils/cvss31/cvsscalc31';

// Constants
const PREVIEW_UPDATE_DELAY = 500;
const SEVERITY_CATEGORIES = ['Critical', 'High', 'Medium', 'Low', 'Info'] as const;
const STATUS_TYPES = ['Open', 'Closed', 'Total'] as const;
const MIN_EDITOR_WIDTH = 320;
const MAX_EDITOR_WIDTH = 700;
const DEFAULT_EDITOR_WIDTH = '50%';

// Utility functions (copy from PDFEditor)
const normalizeSeverityCategory = (category: string) => {
  const normalized = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  return normalized as (typeof SEVERITY_CATEGORIES)[number];
};

const getDefaultDisclaimer = (companyName: string) => `
  <p>
    Capture The Bug Ltd. has prepared this document exclusively for ${companyName}.
    Copying, or modification of this document is strictly prohibited without Capture The Bug Ltd.'s written consent, 
    except for specific purposes when such permission is granted. This document is confidential and proprietary material 
    of Capture The Bug Ltd. and must always be treated as such, not to be disclosed to third parties without prior consent.
  </p>
  
  <p>
    The information within is provided 'as-is,' without any warranty. Vulnerability assessments offer a snapshot of 
    security at a specific moment and are not exhaustive risk assessments. New vulnerabilities may emerge post-assessment. 
    Thus, this report is a guide, not a definitive risk analysis.
  </p>
  
  <p>
    Capture The Bug Ltd. assumes no liability for any changes, omissions, or errors in this document. 
    Capture The Bug Ltd. shall not be liable for any damages, financial or otherwise, arising out of the use or misuse 
    of this report by any current employee of ${companyName} or any member of the general public.
  </p>
`;

const getDefaultOpenCloseCounts = () => ({
  Critical: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
  High: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
  Medium: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
  Low: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
  Info: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 }
});

type NotificationState = { type: string; title: string; message: string; isVisible: boolean };

export function usePDFEditorLogic() {
  const { report_id } = useParams<{ report_id: string }>();
  const { role } = useUserCredentials();

  // State
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [activeSection, setActiveSection] = useState<string>('cover');
  const [sectionData, setSectionData] = useState<Partial<ReportData>>({});
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [notification, setNotification] = useState<NotificationState>({ type: 'info', title: '', message: '', isVisible: false });
  const [isFullViewOpen, setIsFullViewOpen] = useState(false);
  const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);
  const [changeLog, setChangeLog] = useState<any[]>([]);
  const [changeLogLoading, setChangeLogLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<'full' | 'technical' | 'executive' | undefined>(undefined);
  const [editorWidth, setEditorWidth] = useState<string>(DEFAULT_EDITOR_WIDTH);
  const dragging = useRef(false);
  const [viewMode, setViewMode] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const currentData = reportData;
  const [saveLoading, setSaveLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [templateConfirmOpen, setTemplateConfirmOpen] = useState(false);
  const [originalReportData, setOriginalReportData] = useState<ReportData | null>(null);

  // Notification helpers
  const showNotification = useCallback((type: 'success' | 'error' | 'info', title: string, message: string) => {
    setNotification({ type, title, message, isVisible: true });
  }, []);
  const hideNotification = useCallback(() => {
    setNotification((prev: NotificationState) => ({ ...prev, isVisible: false }));
  }, []);

  const previewAbortRef = useRef<AbortController | null>(null);

  // Preview update (used for FullViewModal and save/approve confirmations)
  const updatePreview = useCallback(async (data: ReportData) => {
    if (!report_id) return;
    try {
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
      }
      previewAbortRef.current = new AbortController();
      setPreviewLoading(true);
      const response = await axios.post(
        `/v2/program-reports/${report_id}/preview`,
        { reportData: data },
        {
          headers: { 'Accept': 'text/html' },
          responseType: 'text',
          signal: previewAbortRef.current.signal
        }
      );
      setPreviewHtml(response.data);
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || (axios as any).isCancel?.(error)) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Error updating preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [report_id]);

  // Full view modal
  const openFullView = useCallback(() => {
    if (reportData) {
      updatePreview(reportData);
    }
    setIsFullViewOpen(true);
  }, [reportData, updatePreview]);
  const closeFullView = useCallback(() => setIsFullViewOpen(false), []);

  // Change log
  const openChangeLog = useCallback(async () => {
    if (!report_id) return;
    setIsChangeLogOpen(true);
    setChangeLogLoading(true);
    try {
      const res = await getProgramReportChangeLog(report_id);
      if (res.success) {
        setChangeLog(res.data);
      } else {
        setChangeLog([]);
      }
    } catch (e) {
      setChangeLog([]);
    } finally {
      setChangeLogLoading(false);
    }
  }, [report_id]);
  const closeChangeLog = useCallback(() => setIsChangeLogOpen(false), []);

  const debouncedUpdatePreview = useCallback((_data?: ReportData) => {}, []);

  // Data update
  const handleDataUpdate = useCallback((updates: Partial<ReportData>) => {
    if (!reportData) return;
    let updatedData = { ...reportData, ...updates };

    // If findings or reports_list changed, re-calculate severity counts
    if (updates.detailed_findings || updates.reports_list) {
      const newCounts = {
        ...updatedData.open_close_counts_by_severity,
        ...calculateSeverityCounts(updatedData)
      };

      const totalOpen = Object.values(newCounts).reduce((sum, c) => sum + (c.Open || 0), 0);
      const totalClosed = Object.values(newCounts).reduce((sum, c) => sum + (c.Closed || 0), 0);
      const totalBAR = Object.values(newCounts).reduce((sum, c) => sum + (c.BusinessAcceptsRisk || 0), 0);
      const totalBNA = Object.values(newCounts).reduce((sum, c) => sum + (c.BusinessNonActionable || 0), 0);
      const totalFindings = totalOpen + totalClosed + totalBAR + totalBNA;

      updatedData = {
        ...updatedData,
        open_close_counts_by_severity: newCounts,
        total_open: totalOpen,
        total_closed: totalClosed,
        total_business_accepts_risk: totalBAR,
        total_business_non_actionable: totalBNA,
        total_findings: totalFindings,
        status_counts: {
          Open: totalOpen,
          Closed: totalClosed,
          BusinessAcceptsRisk: totalBAR,
          BusinessNonActionable: totalBNA
        }
      };
    }

    setReportData(updatedData);
    setSectionData(prev => ({ ...prev, ...updates }));
  }, [reportData]);

  // Data fetching
  const fetchReportData = useCallback(async () => {
    if (!report_id) return;
    try {
      setLoading(true);
      const response = await getProgramReportById(report_id);
      if (response) {
        // If response is nested under success structure, get response.data, else use response directly
        let data = response.status === 'success' && response.data ? response.data : response;
        
        // Extract run_id/runId from top level response if present but missing in data
        const runIdVal = response.run_id !== undefined ? response.run_id : (response.runId !== undefined ? response.runId : (data.run_id !== undefined ? data.run_id : data.runId));
        if (runIdVal !== undefined && runIdVal !== null) {
          data.run_id = runIdVal;
          data.runId = runIdVal;
        }

        if (data) {
          data = filterReportDataByRunId(data);
        }

        // Debug: Log what we loaded from backend
        console.log('=== FETCH DEBUG ===');
        console.log('Raw response:', response);
        console.log('Loaded detailed_findings:', data?.detailed_findings?.length || 0, 'findings');
        console.log('First finding loaded:', data?.detailed_findings?.[0]);
        console.log('Report run_id:', data?.run_id);

        // Robustly handle methodology as stringified JSON or object
        if (typeof data.methodology === 'string') {
          const trimmed = data.methodology.trim();
          if (trimmed.startsWith('{')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (typeof parsed === 'object' && parsed !== null) {
                data.methodology = parsed;
              }
            } catch (e) {}
          } else if (trimmed.length > 0) {
            // Check if it's a legacy CSV list of options (e.g. "web,network") or custom text/HTML description.
            // If it has spaces, HTML tags, or length > 50, it is a custom description, so keep it as a string.
            const isCustomText = trimmed.includes(' ') || trimmed.includes('<') || trimmed.length > 50;
            if (!isCustomText) {
              data.methodology = trimmed.split(',').reduce((acc: any, key: string) => {
                acc[key.trim()] = true;
                return acc;
              }, {});
            }
          }
        }
        // Patch: Ensure methodology default is always set if missing or empty
        if (
          !data.methodology ||
          (typeof data.methodology === 'object' &&
            !data.methodology.web &&
            !data.methodology.network &&
            !data.methodology.mobile)
        ) {
          data.methodology = { web: true, network: false, mobile: false };
        }
        // Forced re-calculation of all counts from the findings list to ensure accuracy
        const newCounts = calculateSeverityCounts(data);
        const totalOpen = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.Open || 0), 0);
        const totalClosed = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.Closed || 0), 0);
        const totalBAR = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.BusinessAcceptsRisk || 0), 0);
        const totalBNA = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.BusinessNonActionable || 0), 0);

        data = {
          ...data,
          open_close_counts_by_severity: newCounts,
          total_open: totalOpen,
          total_closed: totalClosed,
          total_business_accepts_risk: totalBAR,
          total_business_non_actionable: totalBNA,
          total_findings: totalOpen + totalClosed + totalBAR + totalBNA,
          status_counts: {
            Open: totalOpen,
            Closed: totalClosed,
            BusinessAcceptsRisk: totalBAR,
            BusinessNonActionable: totalBNA
          }
        };

        setReportData(data);
        setOriginalReportData({
          template: data.template,
          finding_config: data.finding_config ? { is_template_locked: data.finding_config?.is_template_locked } : undefined
        } as any);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  }, [report_id]);

  useEffect(() => {
    if (report_id) {
      fetchReportData();
    }
  }, [report_id, fetchReportData]);


  // Set preview mode based on report data when it loads
  useEffect(() => {
    if (reportData && previewMode === undefined) {
      const appropriateMode = getAppropriatePreviewMode(reportData);
      console.log('usePDFEditorLogic - Setting initial preview mode:', appropriateMode, 'based on reportData');
      setPreviewMode(appropriateMode);
    }
  }, [reportData, previewMode]);


  useEffect(() => {
    // Patch: Ensure methodology default is always set if missing or empty for new reports (no report_id)
    if (!report_id && reportData) {
      if (
        !reportData.methodology ||
        (typeof reportData.methodology === 'object' &&
          !reportData.methodology.web &&
          !reportData.methodology.network &&
          !reportData.methodology.mobile)
      ) {
        const patched = { ...reportData, methodology: { web: true, network: false, mobile: false } };
        setReportData(patched);
      }
    }
  }, [report_id, reportData]);

  // Status update
  const updateReportStatusHandler = useCallback(async (newStatus: ProgramReportStatus) => {
    if (!report_id) return;
    try {
      setStatusUpdateLoading(true);
      const response = await updateReportStatus(report_id, newStatus);
      if (response.success) {
        setReportData(prev => prev ? { ...prev, status: newStatus } : null);
        showNotification('success', 'Report Status Updated', `Report status updated to ${newStatus}`);
      } else {
        showNotification('error', 'Report Status Update Error', 'Failed to update report status. Please try again.');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating report status:', error);
      showNotification('error', 'Report Status Update Error', 'Error updating report status. Please try again.');
    } finally {
      setStatusUpdateLoading(false);
    }
  }, [report_id, showNotification]);

  // Save
  // Save
  const handleSave = useCallback(async (forceLock: any = false) => {
    if (!reportData) return;

    const isLocked = originalReportData?.finding_config?.is_template_locked === true;
    const currentTemplate = sectionData.template !== undefined ? sectionData.template : (reportData.template || 'template-1');
    const originalTemplate = originalReportData?.template || 'template-1';
    const isTemplateChanged = currentTemplate !== originalTemplate;
    const isConfirmed = forceLock === true;

    console.log('=== TEMPLATE LOCK DEBUG ===');
    console.log('originalReportData:', originalReportData);
    console.log('isLocked:', isLocked);
    console.log('currentTemplate:', currentTemplate);
    console.log('originalTemplate:', originalTemplate);
    console.log('isTemplateChanged:', isTemplateChanged);
    console.log('isConfirmed:', isConfirmed);

    if (!isLocked && isTemplateChanged && !isConfirmed) {
      console.log('Showing template confirmation modal...');
      setTemplateConfirmOpen(true);
      return;
    }

    try {
      setSaveLoading(true);
      
      let finalSectionData = { ...sectionData };
      if (isConfirmed || (isTemplateChanged && !isLocked)) {
        const currentConfig = sectionData.finding_config !== undefined 
          ? sectionData.finding_config 
          : (reportData.finding_config || {});
        finalSectionData.finding_config = {
          ...currentConfig,
          is_template_locked: true
        } as any;
      }

      const updatedData = { ...reportData, ...finalSectionData };

      // Debug: Log what we're sending to backend
      console.log('=== SAVE DEBUG ===');
      console.log('reportData.detailed_findings:', reportData.detailed_findings?.length || 0, 'findings');
      console.log('sectionData.detailed_findings:', sectionData.detailed_findings?.length || 0, 'findings');
      console.log('updatedData.detailed_findings:', updatedData.detailed_findings?.length || 0, 'findings');
      console.log('First finding in updatedData:', updatedData.detailed_findings?.[0]);

      const response = await updateProgramReport(report_id!, updatedData);

      // Use the response data from backend to ensure we have the latest state.
      // If the backend returned fewer/no findings than what we sent (e.g. due to the
      // freshData empty-array bug in formatReportPayload), fall back to updatedData
      // for the findings arrays so we never lose findings after a save.
      let rawSavedData = response.success ? response.data : updatedData;

      // Safety net: if the backend response has fewer findings than what we sent,
      // preserve the findings we sent (they were already saved to DB correctly).
      const sentFindingsCount = updatedData.detailed_findings?.length || 0;
      const backendFindingsCount = rawSavedData?.detailed_findings?.length || 0;
      if (sentFindingsCount > 0 && backendFindingsCount === 0) {
        console.warn('[handleSave] Backend returned 0 findings but we sent', sentFindingsCount,
          '— keeping local findings to prevent data loss. This indicates a formatReportPayload bug.');
        rawSavedData = {
          ...rawSavedData,
          detailed_findings: updatedData.detailed_findings,
          reports_list: updatedData.reports_list ?? rawSavedData?.reports_list
        };
      }

      // Debug: Log what we got back from backend
      console.log('rawSavedData.detailed_findings (raw from backend):', rawSavedData.detailed_findings?.length || 0, 'findings');
      console.log('First finding in rawSavedData:', rawSavedData.detailed_findings?.[0]);

      // Apply the same post-processing pipeline as fetchReportData to keep state consistent:
      // 1. Filter by run_id (so findings don't appear/disappear differently after save vs. load)
      let savedData = rawSavedData;
      if (savedData) {
        savedData = filterReportDataByRunId(savedData);
      }

      // 2. Recalculate severity counts from findings to keep counts accurate
      const newCounts = calculateSeverityCounts(savedData);
      const totalOpen = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.Open || 0), 0);
      const totalClosed = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.Closed || 0), 0);
      const totalBAR = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.BusinessAcceptsRisk || 0), 0);
      const totalBNA = Object.values(newCounts).reduce((sum: number, c: any) => sum + (c.BusinessNonActionable || 0), 0);
      savedData = {
        ...savedData,
        open_close_counts_by_severity: newCounts,
        total_open: totalOpen,
        total_closed: totalClosed,
        total_business_accepts_risk: totalBAR,
        total_business_non_actionable: totalBNA,
        total_findings: totalOpen + totalClosed + totalBAR + totalBNA,
        status_counts: {
          Open: totalOpen,
          Closed: totalClosed,
          BusinessAcceptsRisk: totalBAR,
          BusinessNonActionable: totalBNA
        }
      };

      console.log('savedData.detailed_findings (after post-processing):', savedData.detailed_findings?.length || 0, 'findings');

      setReportData(savedData);
      setOriginalReportData({
        template: savedData.template,
        finding_config: savedData.finding_config ? { is_template_locked: savedData.finding_config?.is_template_locked } : undefined
      } as any);
      setSectionData({});
      showNotification('success', 'Report Saved', 'Report saved successfully!');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error saving report:', error);

      // Check if it's a 413 error (payload too large)
      if (error?.response?.status === 413) {
        showNotification(
          'error',
          'Report Too Large',
          'The report data is too large to save. This is usually caused by server configuration limits. Please contact your system administrator to increase the nginx client_max_body_size setting. See NGINX_CONFIGURATION_FIX.md for details.'
        );
      } else {
        showNotification('error', 'Report Save Error', `Error saving report: ${error?.response?.data?.message || error?.message || 'Please try again.'}`);
      }
    } finally {
      setSaveLoading(false);
    }
  }, [report_id, reportData, sectionData, originalReportData, showNotification]);

  // Approve
  const handleApprove = useCallback(async () => {
    if (!reportData) return;
    try {
      setApproveLoading(true);
      // Always save first, even if there are no unsaved changes
      const updatedData = { ...reportData, ...sectionData };
      // Step 1: Save the report data
      const response = await updateProgramReport(report_id!, updatedData);
      let rawApproveData = response.success ? response.data : updatedData;
      // Safety net: preserve findings if backend returned empty
      const sentCount = updatedData.detailed_findings?.length || 0;
      if (sentCount > 0 && (rawApproveData?.detailed_findings?.length || 0) === 0) {
        rawApproveData = { ...rawApproveData, detailed_findings: updatedData.detailed_findings, reports_list: updatedData.reports_list ?? rawApproveData?.reports_list };
      }
      let approveData = rawApproveData;
      if (approveData) approveData = filterReportDataByRunId(approveData);
      const approveCounts = calculateSeverityCounts(approveData);
      const aOpen = Object.values(approveCounts).reduce((s: number, c: any) => s + (c.Open || 0), 0);
      const aClosed = Object.values(approveCounts).reduce((s: number, c: any) => s + (c.Closed || 0), 0);
      const aBAR = Object.values(approveCounts).reduce((s: number, c: any) => s + (c.BusinessAcceptsRisk || 0), 0);
      const aBNA = Object.values(approveCounts).reduce((s: number, c: any) => s + (c.BusinessNonActionable || 0), 0);
      approveData = { ...approveData, open_close_counts_by_severity: approveCounts, total_open: aOpen, total_closed: aClosed, total_business_accepts_risk: aBAR, total_business_non_actionable: aBNA, total_findings: aOpen + aClosed + aBAR + aBNA, status_counts: { Open: aOpen, Closed: aClosed, BusinessAcceptsRisk: aBAR, BusinessNonActionable: aBNA } };
      setReportData(approveData);
      setSectionData({});
      // Step 2: Determine the new status based on role and current status
      let newStatus: ProgramReportStatus | null = null;
      let roleStr = '';
      const ROLE_MAP: Record<number, string> = {
        1: 'RESEARCHER',
        2: 'BUSINESS',
        3: 'ADMIN',
        4: 'QA',
        5: 'ADMIN_MANAGER',
        6: 'DEVELOPER',
        7: 'BUSINESS_MANAGER',
        8: 'BUSINESS_ADMINISTRATOR',
        9: 'SUB_ADMIN',
      };
      if (typeof role === 'number') {
        roleStr = ROLE_MAP[role] || '';
      } else if (typeof role === 'string') {
        roleStr = role;
      }
      roleStr = roleStr.toUpperCase();
      const statusStr = (reportData.status || '').toLowerCase();
      if (roleStr === 'QA') {
        if (statusStr === 'draft' || statusStr === 'qa_review') {
          newStatus = 'admin_review';
        } else if (statusStr === 'business_requested_changes') {
          newStatus = 'changes_added';
        }
      } else if ([
        'ADMIN',
        'SUB_ADMIN',
        'ADMIN_MANAGER'
      ].includes(roleStr)) {
        if (statusStr === 'admin_review' || statusStr === 'business_requested_changes') {
          newStatus = 'approved';
        } else if (statusStr === 'changes_added') {
          newStatus = 'report_updated';
        }
      }
      // Step 3: Update the status only after successful save
      if (newStatus) {
        await updateReportStatusHandler(newStatus);
      }
      showNotification('success', 'Report Approved', 'Report saved and approved successfully!');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error approving report:', error);
      showNotification('error', 'Report Approval Error', 'Error saving or approving report. Please try again.');
    } finally {
      setApproveLoading(false);
    }
  }, [report_id, reportData, sectionData, updateReportStatusHandler, role, showNotification]);

  // Reject
  const handleReject = useCallback(async () => {
    if (!reportData) return;
    try {
      setRejectLoading(true);
      const updatedData = { ...reportData, ...sectionData };
      const response = await updateProgramReport(report_id!, updatedData);
      let rawRejectData = response.success ? response.data : updatedData;
      // Safety net: preserve findings if backend returned empty
      const rSentCount = updatedData.detailed_findings?.length || 0;
      if (rSentCount > 0 && (rawRejectData?.detailed_findings?.length || 0) === 0) {
        rawRejectData = { ...rawRejectData, detailed_findings: updatedData.detailed_findings, reports_list: updatedData.reports_list ?? rawRejectData?.reports_list };
      }
      let rejectData = rawRejectData;
      if (rejectData) rejectData = filterReportDataByRunId(rejectData);
      setReportData(rejectData);
      setSectionData({});
      await updateReportStatusHandler('draft');
      showNotification('success', 'Report Rejected', 'Report rejected and sent back to QA for editing!');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error rejecting report:', error);
      showNotification('error', 'Report Rejection Error', 'Error rejecting report. Please try again.');
    } finally {
      setRejectLoading(false);
    }
  }, [report_id, reportData, sectionData, updateReportStatusHandler, showNotification]);

  // Permissions
  const canApprove = useMemo(() => {
    if (!reportData) return false;
    let roleStr = '';
    const ROLE_MAP: Record<number, string> = {
      1: 'RESEARCHER',
      2: 'BUSINESS',
      3: 'ADMIN',
      4: 'QA',
      5: 'ADMIN_MANAGER',
      6: 'DEVELOPER',
      7: 'BUSINESS_MANAGER',
      8: 'BUSINESS_ADMINISTRATOR',
      9: 'SUB_ADMIN',
    };
    if (typeof role === 'number') {
      roleStr = ROLE_MAP[role] || '';
    } else if (typeof role === 'string') {
      roleStr = role;
    }
    roleStr = roleStr.toUpperCase();
    const statusStr = (reportData.status || '').toLowerCase();
    if (roleStr === 'QA') {
      return statusStr === 'draft' || statusStr === 'qa_review' || statusStr === 'business_requested_changes';
    }
    if ([
      'ADMIN',
      'SUB_ADMIN',
      'ADMIN_MANAGER'
    ].includes(roleStr)) {
      return statusStr === 'admin_review' || statusStr === 'changes_added' || statusStr === 'business_requested_changes';
    }
    return false;
  }, [role, reportData]);

  const canReject = useMemo(() => {
    if (!reportData) return false;
    let roleStr = '';
    const ROLE_MAP: Record<number, string> = {
      1: 'RESEARCHER',
      2: 'BUSINESS',
      3: 'ADMIN',
      4: 'QA',
      5: 'ADMIN_MANAGER',
      6: 'DEVELOPER',
      7: 'BUSINESS_MANAGER',
      8: 'BUSINESS_ADMINISTRATOR',
      9: 'SUB_ADMIN',
    };
    if (typeof role === 'number') {
      roleStr = ROLE_MAP[role] || '';
    } else if (typeof role === 'string') {
      roleStr = role;
    }
    return [
      'ADMIN',
      'SUB_ADMIN',
      'ADMIN_MANAGER'
    ].includes(roleStr.toUpperCase()) && reportData.status === 'admin_review';
  }, [role, reportData]);

  // Section handlers (pass-through)
  const handleInputChange = useCallback((field: keyof ReportData, value: string) => {
    handleDataUpdate({ [field]: value });
  }, [handleDataUpdate]);
  const handleHtmlChange = useCallback((field: keyof ReportData, value: string) => {
    handleDataUpdate({ [field]: value });
  }, [handleDataUpdate]);
  const handleDataChange = useCallback((field: string, value: any) => {
    if (field === 'template') {
      const isV2 = value === 'template-2';
      const allowedV2 = ['branding', 'cover', 'executive_summary', 'scope', 'findings_summary', 'vulnerability_ratings', 'owasp_top_10', 'finding_config', 'critical_findings', 'high_findings', 'medium_findings', 'low_findings', 'info_findings', 'remediation_roadmap', 'methodology', 'conclusion', 'tools_approach', 'certificate', 'attestation_letter'];
      const allowedV1 = ['branding', 'cover', 'disclaimer', 'document_reference', 'executive_summary', 'recommendations', 'scope', 'project_objectives', 'findings_summary', 'vulnerability_ratings', 'key_findings', 'finding_config', 'critical_findings', 'high_findings', 'medium_findings', 'low_findings', 'info_findings', 'methodology_selector', 'methodology', 'certificate', 'attestation_letter'];
      
      if (isV2 && !allowedV2.includes(activeSection)) {
        setActiveSection('cover');
      } else if (!isV2 && !allowedV1.includes(activeSection)) {
        setActiveSection('cover');
      }
    }
    handleDataUpdate({ [field]: value });
  }, [handleDataUpdate, activeSection, setActiveSection]);
  const handleFindingChange = useCallback((index: number, field: keyof DetailedFinding, value: string) => {
    if (!reportData) return;
    const updatedFindings = [...(reportData.detailed_findings || [])];

    // Direct index access - the BaseFindingEditor now passes the actual index
    if (index >= 0 && index < updatedFindings.length) {
      const oldValue = updatedFindings[index][field];
      updatedFindings[index] = {
        ...updatedFindings[index],
        [field]: value
      };

      if (field === 'severity_category') {
        const newSev = value;
        const newPrefix = (newSev || 'Info').trim().charAt(0).toUpperCase();
        const currentAbbr = (updatedFindings[index].abbreviation || '').trim();

        if (!currentAbbr || /^[CHMLI]\d+$/i.test(currentAbbr)) {
          const countSameSev = updatedFindings.filter((f, fIdx) => {
            if (fIdx === index) return false;
            const fSev = (f.severity_category || 'Info').trim().toLowerCase();
            return fSev === newSev.toLowerCase();
          }).length;
          updatedFindings[index].abbreviation = `${newPrefix}${countSameSev + 1}`;
        }
      }

      if (field === 'cvss_vector') {
        const newVector = value;
        if (newVector && newVector.includes('CVSS')) {
          try {
            const parsed = parseCVSSVector(newVector);
            if (parsed && parsed.score && parsed.score !== '-.-') {
              updatedFindings[index].cvss_score = parsed.score;
              updatedFindings[index].cvss_version = parsed.cvss_version || (newVector.startsWith('CVSS:4.0') ? '4.0' : '3.1');
              updatedFindings[index].severity_category = normalizeSeverityCategory(parsed.category || 'Info');
            }
          } catch (_) {}
        }
      }

      if (field === 'cvss_score') {
        const numScore = Number(value);
        if (!isNaN(numScore)) {
          let calculatedSev: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info' = 'Info';
          if (numScore >= 9.0) calculatedSev = 'Critical';
          else if (numScore >= 7.0) calculatedSev = 'High';
          else if (numScore >= 4.0) calculatedSev = 'Medium';
          else if (numScore >= 0.1) calculatedSev = 'Low';
          updatedFindings[index].severity_category = calculatedSev;
        }
      }

      // Sync matching item in reports_list if present
      let updatedReportsList = reportData.reports_list;
      if (reportData.reports_list && reportData.reports_list.length > 0) {
        const targetFinding = updatedFindings[index];
        const targetKey = getFindingStableKey(targetFinding, index);
        const oldAbbr = (oldValue && field === 'abbreviation' ? String(oldValue) : '').trim().toLowerCase();
        const oldTitle = (oldValue && field === 'title' ? String(oldValue) : '').trim().toLowerCase();

        updatedReportsList = reportData.reports_list.map((r, rIdx) => {
          const rKey = getFindingStableKey(r, rIdx);
          const rAbbr = (r.abbreviation || '').trim().toLowerCase();
          const rTitle = (r.title || '').trim().toLowerCase();
          const fAbbr = (targetFinding.abbreviation || '').trim().toLowerCase();
          const fTitle = (targetFinding.title || '').trim().toLowerCase();

          const isMatch = rKey === targetKey || 
            (fAbbr && rAbbr === fAbbr) || 
            (oldAbbr && rAbbr === oldAbbr) ||
            (fTitle && rTitle === fTitle) ||
            (oldTitle && rTitle === oldTitle) ||
            (reportData.reports_list?.length === reportData.detailed_findings?.length && rIdx === index);

          if (!isMatch) return r;
          const updatedObj = { ...r, [field]: value };
          if (field === 'severity_category' && targetFinding.abbreviation) {
            updatedObj.abbreviation = targetFinding.abbreviation;
          }
          return updatedObj;
        });
      }

      // Debug: Log the change
      console.log(`=== FINDING CHANGE DEBUG ===`);
      console.log(`Changed finding ${index}, field "${field}"`);
      console.log(`Old value: "${oldValue}"`);
      console.log(`New value: "${value}"`);
      console.log(`Total findings: ${updatedFindings.length}`);

      handleDataUpdate({
        detailed_findings: updatedFindings,
        ...(updatedReportsList !== reportData.reports_list ? { reports_list: updatedReportsList } : {})
      });
    }
  }, [reportData, handleDataUpdate]);
  const handleAddFinding = useCallback((severity: string) => {
    if (!reportData) return;
    const severityAbbreviations: Record<string, string> = { Critical: 'C', High: 'H', Medium: 'M', Low: 'L', Info: 'I' };

    // Robust severity matching - count findings of this severity using same logic as BaseFindingEditor
    const findingsOfThisSeverity = (reportData.detailed_findings || []).filter(f => {
      if (!f.severity_category) return false;
      return f.severity_category === severity ||
        f.severity_category.toLowerCase() === severity.toLowerCase() ||
        f.severity_category.toUpperCase() === severity.toUpperCase();
    });

    const newCount = findingsOfThisSeverity.length + 1;
    const abbreviation = `${severityAbbreviations[severity]}${newCount}`;

    // Determine the severity format to use based on existing data
    let severityToUse = severity;
    if (reportData.detailed_findings && reportData.detailed_findings.length > 0) {
      // Use the same format as existing findings
      const existingSeverity = reportData.detailed_findings.find(f =>
        f.severity_category && f.severity_category.toLowerCase() === severity.toLowerCase()
      )?.severity_category;
      if (existingSeverity) {
        severityToUse = existingSeverity;
      }
    }

    const newFinding: DetailedFinding = {
      abbreviation: abbreviation,
      title: 'New Finding',
      severity_category: severityToUse as 'Critical' | 'High' | 'Medium' | 'Low' | 'Info',
      status: 'Open',
      scope: '',
      description: '',
      instructions: '',
      impact: '',
      fix: '',
      submitted_date: new Date().toISOString()
    };
    const updatedFindings = [...(reportData.detailed_findings || []), newFinding];
    handleDataUpdate({ detailed_findings: updatedFindings });
  }, [reportData, handleDataUpdate]);
  const handleRemoveFinding = useCallback((index: number) => {
    if (!reportData) return;
    const updatedFindings = [...(reportData.detailed_findings || [])];

    // Direct index access - the BaseFindingEditor now passes the actual index
    if (index >= 0 && index < updatedFindings.length) {
      const targetFinding = updatedFindings[index];
      updatedFindings.splice(index, 1);

      // Sync with reports_list if present
      let updatedReportsList = reportData.reports_list;
      if (reportData.reports_list && reportData.reports_list.length > 0 && targetFinding) {
        const targetKey = getFindingStableKey(targetFinding, index);
        const fAbbr = (targetFinding.abbreviation || '').trim().toLowerCase();
        const fTitle = (targetFinding.title || '').trim().toLowerCase();

        updatedReportsList = reportData.reports_list.filter((r, rIdx) => {
          const rKey = getFindingStableKey(r, rIdx);
          const rAbbr = (r.abbreviation || '').trim().toLowerCase();
          const rTitle = (r.title || '').trim().toLowerCase();

          const isMatch = rKey === targetKey || 
            (fAbbr && rAbbr === fAbbr) || 
            (fTitle && rTitle === fTitle) ||
            (reportData.reports_list?.length === reportData.detailed_findings?.length && rIdx === index);

          return !isMatch;
        });
      }

      handleDataUpdate({
        detailed_findings: updatedFindings,
        ...(updatedReportsList !== reportData.reports_list ? { reports_list: updatedReportsList } : {})
      });
    }
  }, [reportData, handleDataUpdate]);
  const handleTargetDetailsChange = useCallback((index: number, field: string, value: any) => {
    if (!reportData) return;
    const oldTarget = (reportData.target_details || [])[index];
    
    let updatedFields: any = { [field]: value };
    if (field === 'roles' && Array.isArray(value)) {
      updatedFields = {
        roles: value,
        role: value[0]?.role || '',
        privilege_level: value[0]?.privilege_level || '',
        credentials: value[0]?.credentials || ''
      };
    }
    
    const newTargets = (reportData.target_details || []).map((target, i) => i === index ? { ...target, ...updatedFields } : target);

    // Safeguard & Reconciliation: If the target's URL/value changes, update any linked or matching findings' scope strings
    let updatedFindings = reportData.detailed_findings || [];
    if (oldTarget && (field === 'target_value' || field === 'url')) {
      const oldVal = oldTarget.target_value || oldTarget.url || '';
      if (oldVal && oldVal !== value) {
        updatedFindings = (reportData.detailed_findings || []).map(finding => {
          let updatedFinding = { ...finding };
          let changed = false;

          // Case 1: Linked by ID (V2 targets) - update finding.scope for local sync/fallback
          if (oldTarget.target_id && finding.target_ids && finding.target_ids.includes(oldTarget.target_id)) {
            if (finding.scope && finding.scope.includes(oldVal)) {
              updatedFinding.scope = finding.scope.split(oldVal).join(value);
              changed = true;
            }
          }
          // Case 2: Legacy fallback (V1 findings) - Match by exact string match of target URL in finding.scope
          else if (finding.scope && finding.scope.includes(oldVal)) {
            updatedFinding.scope = finding.scope.split(oldVal).join(value);
            changed = true;
          }

          return changed ? updatedFinding : finding;
        });
      }
    }

    handleDataUpdate({
      target_details: newTargets,
      ...(updatedFindings !== reportData.detailed_findings ? { detailed_findings: updatedFindings } : {})
    });
  }, [reportData, handleDataUpdate]);
  const handleAddTarget = useCallback(() => {
    if (!reportData) return;
    const newTargets = [...(reportData.target_details || []), { type: '', url: '' }];
    handleDataUpdate({ target_details: newTargets });
  }, [reportData, handleDataUpdate]);
  const handleRemoveTarget = useCallback((index: number) => {
    if (!reportData) return;
    const newTargets = (reportData.target_details || []).filter((_, i) => i !== index);
    handleDataUpdate({ target_details: newTargets });
  }, [reportData, handleDataUpdate]);

  const handleTableChange = useCallback((fieldPath: string, value: number) => {
    if (!reportData) return;

    // Split the path (e.g., "open_close_counts_by_severity.Critical.Open")
    const parts = fieldPath.split('.');
    if (parts.length === 3 && parts[0] === 'open_close_counts_by_severity') {
      const [, severity, key] = parts;
      const prevCounts: any = reportData.open_close_counts_by_severity || {};
      const prevSevCounts = prevCounts[severity] || {};
      const updatedCounts = {
        ...prevCounts,
        [severity]: {
          ...prevSevCounts,
          [key]: value
        }
      };
      setReportData({
        ...reportData,
        open_close_counts_by_severity: updatedCounts
      });
      return;
    }

    // Generic shallow clone fallback for other nested paths without full serialization
    const updatedData: any = { ...reportData };
    let current: any = updatedData;
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = { ...(current[parts[i]] || {}) };
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    setReportData(updatedData);
  }, [reportData]);

  const handleKeyFindingChange = useCallback((index: number, field: string, value: string) => {
    if (!reportData) return;
    const updatedReports = [...(reportData.reports_list || [])];
    const targetFinding = updatedReports[index];
    if (targetFinding) {
      const oldValue = targetFinding[field as keyof typeof targetFinding];
      updatedReports[index] = { ...updatedReports[index], [field]: value };
      
      if (field === 'severity_category') {
        const newSev = value;
        const newPrefix = (newSev || 'Info').trim().charAt(0).toUpperCase();
        const currentAbbr = (targetFinding.abbreviation || '').trim();

        if (!currentAbbr || /^[CHMLI]\d+$/i.test(currentAbbr)) {
          const countSameSev = updatedReports.filter((r, rIdx) => {
            if (rIdx === index) return false;
            const rSev = (r.severity_category || 'Info').trim().toLowerCase();
            return rSev === newSev.toLowerCase();
          }).length;
          updatedReports[index].abbreviation = `${newPrefix}${countSameSev + 1}`;
        }
      }

      let updatedDetailed = reportData.detailed_findings;
      if (reportData.detailed_findings && reportData.detailed_findings.length > 0) {
        const mutatedFinding = updatedReports[index];
        const targetKey = getFindingStableKey(mutatedFinding, index);
        const fAbbr = (mutatedFinding.abbreviation || '').trim().toLowerCase();
        const fTitle = (mutatedFinding.title || '').trim().toLowerCase();
        const oldAbbr = (oldValue && field === 'abbreviation' ? String(oldValue) : '').trim().toLowerCase();
        const oldTitle = (oldValue && field === 'title' ? String(oldValue) : '').trim().toLowerCase();

        updatedDetailed = reportData.detailed_findings.map((df, dfIdx) => {
          const dfKey = getFindingStableKey(df, dfIdx);
          const dfAbbr = (df.abbreviation || '').trim().toLowerCase();
          const dfTitle = (df.title || '').trim().toLowerCase();

          const isMatch = dfKey === targetKey || 
            (fAbbr && dfAbbr === fAbbr) || 
            (oldAbbr && dfAbbr === oldAbbr) ||
            (fTitle && dfTitle === fTitle) ||
            (oldTitle && dfTitle === oldTitle) ||
            (reportData.reports_list?.length === reportData.detailed_findings?.length && dfIdx === index);

          if (!isMatch) return df;
          const updatedObj = { ...df, [field]: value };
          if (field === 'severity_category' && mutatedFinding.abbreviation) {
            updatedObj.abbreviation = mutatedFinding.abbreviation;
          }
          return updatedObj;
        });
      }

      handleDataUpdate({
        reports_list: updatedReports,
        ...(updatedDetailed !== reportData.detailed_findings ? { detailed_findings: updatedDetailed } : {})
      });
    }
  }, [reportData, handleDataUpdate]);
  const handleAddKeyFinding = useCallback(() => {
    if (!reportData) return;
    const severityAbbreviations: Record<string, string> = { Critical: 'C', High: 'H', Medium: 'M', Low: 'L', Info: 'I' };
    const defaultSeverity = 'Medium' as const;
    const severityCounts: Record<string, number> = { Critical: 0, High: 0, Medium: 0, Low: 0, Info: 0 };
    (reportData.reports_list || []).forEach(finding => {
      if (finding.severity_category && finding.severity_category in severityCounts) {
        severityCounts[finding.severity_category as keyof typeof severityCounts]++;
      }
    });
    const newCount = severityCounts[defaultSeverity] + 1;
    const abbreviation = `${severityAbbreviations[defaultSeverity]}${newCount}`;
    const newFinding = {
      abbreviation: abbreviation,
      title: 'New Finding',
      severity_category: defaultSeverity as 'Critical' | 'High' | 'Medium' | 'Low' | 'Info',
      status: 'Open'
    };
    const updatedReports = [...(reportData.reports_list || []), newFinding];
    handleDataUpdate({ reports_list: updatedReports });
  }, [reportData, handleDataUpdate]);
  const handleRemoveKeyFinding = useCallback((index: number) => {
    if (!reportData) return;
    const targetFinding = reportData.reports_list?.[index];
    const updatedReports = reportData.reports_list?.filter((_, i) => i !== index);

    let updatedDetailed = reportData.detailed_findings;
    if (targetFinding && reportData.detailed_findings && reportData.detailed_findings.length > 0) {
      const targetKey = getFindingStableKey(targetFinding, index);
      const fAbbr = (targetFinding.abbreviation || '').trim().toLowerCase();
      const fTitle = (targetFinding.title || '').trim().toLowerCase();

      updatedDetailed = reportData.detailed_findings.filter((df, dfIdx) => {
        const dfKey = getFindingStableKey(df, dfIdx);
        const dfAbbr = (df.abbreviation || '').trim().toLowerCase();
        const dfTitle = (df.title || '').trim().toLowerCase();

        const isMatch = dfKey === targetKey || 
          (fAbbr && dfAbbr === fAbbr) || 
          (fTitle && dfTitle === fTitle) ||
          (reportData.reports_list?.length === reportData.detailed_findings?.length && dfIdx === index);

        return !isMatch;
      });
    }

    handleDataUpdate({ 
      reports_list: updatedReports,
      ...(updatedDetailed !== reportData.detailed_findings ? { detailed_findings: updatedDetailed } : {})
    });
  }, [reportData, handleDataUpdate]);
  const handleRecommendationsChange = useCallback((recommendations: { title: string; description: string }[]) => {
    handleDataUpdate({ recommendations_list: recommendations });
  }, [handleDataUpdate]);
  const saveSectionChanges = useCallback(async () => {
    if (!report_id || !reportData) return;
    try {
      const updatedData = { ...reportData, ...sectionData };
      const response = await updateProgramReport(report_id!, updatedData);
      const savedData = response.success ? response.data : updatedData;
      setReportData(savedData);
      await updatePreview(savedData);
      setSectionData({});
      showNotification('success', 'Section Changes Saved', 'Section changes saved successfully!');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving section changes:', error);
      showNotification('error', 'Section Changes Error', 'Error saving section changes. Please try again.');
    }
  }, [report_id, reportData, sectionData, updatePreview, showNotification]);

  // Print handler
  const handlePrint = useReactToPrint({
    content: (): HTMLDivElement | null => previewRef.current,
    documentTitle: reportData?.report_title || 'Security Report',
    removeAfterPrint: true,
    pageStyle: `
      @page { size: A4; margin: 0; }
      body { margin: 0; padding: 0; }
      .page { page-break-after: always; min-height: 297mm; }
      .page:last-child { page-break-after: avoid; }
      .no-break, img, table, .finding-card { break-inside: avoid !important; page-break-inside: avoid !important; }
      .page-break { break-after: page !important; page-break-after: always !important; }
    `,
  } as any);

  return {
    report_id,
    role,
    loading,
    previewLoading,
    previewHtml,
    reportData,
    activeSection,
    setActiveSection,
    sectionData,
    statusUpdateLoading,
    notification,
    setNotification,
    isFullViewOpen,
    setIsFullViewOpen,
    isChangeLogOpen,
    setIsChangeLogOpen,
    changeLog,
    setChangeLog,
    changeLogLoading,
    setChangeLogLoading,
    previewMode,
    setPreviewMode,
    editorWidth,
    setEditorWidth,
    dragging,
    viewMode,
    setViewMode,
    previewRef,
    handlePrint,
    showNotification,
    hideNotification,
    openFullView,
    closeFullView,
    openChangeLog,
    closeChangeLog,
    updatePreview,
    debouncedUpdatePreview,
    handleDataUpdate,
    fetchReportData,
    updateReportStatus: updateReportStatusHandler,
    handleSave,
    handleApprove,
    handleReject,
    canApprove,
    canReject,
    handleInputChange,
    handleHtmlChange,
    handleDataChange,
    handleFindingChange,
    handleAddFinding,
    handleRemoveFinding,
    handleTargetDetailsChange,
    handleAddTarget,
    handleRemoveTarget,
    handleTableChange,
    handleKeyFindingChange,
    handleAddKeyFinding,
    handleRemoveKeyFinding,
    handleRecommendationsChange,
    saveSectionChanges,
    currentData,
    saveLoading,
    approveLoading,
    rejectLoading,
    templateConfirmOpen,
    setTemplateConfirmOpen,
    handleCancelTemplateSave: useCallback(() => {
      setTemplateConfirmOpen(false);
      if (reportData) {
        handleDataUpdate({ template: reportData.template || 'template-1' });
      }
    }, [reportData, handleDataUpdate])
  };
} 